// Frontend: SentinelRadar.tsx
import React, { useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { db, auth } from '../config/firebase';
import { collection, getDocs, updateDoc } from 'firebase/firestore';
import { adminUpdateMandantRoot } from '../config/adminOverwriteGuard';
import useAdminStore from '../store/adminStore';
import useAppStore from '../store/appStore';
import { calculateAllMetrics } from '../store/selectors/calculations';
import { sanitizeClientData } from '../store/slices/coreDataSlice';

// --- ICONS ---
const IconBase = ({ children, size = 20, className = "" }: any) => <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>{children}</svg>;
const BellOffIcon = (props: any) => <IconBase {...props}><path d="M13.73 21a2 2 0 0 1-3.46 0"/><path d="M18.63 13A17.89 17.89 0 0 1 18 8"/><path d="M6.26 6.26A5.86 5.86 0 0 0 6 8c0 7-3 9-3 9h14"/><path d="M18 8a6 6 0 0 0-9.33-5"/><line x1="1" y1="1" x2="23" y2="23"/></IconBase>;
const AlertTriangleIcon = (props: any) => <IconBase {...props}><path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></IconBase>;
const XIcon = (props: any) => <IconBase {...props}><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></IconBase>;
const ExternalLinkIcon = (props: any) => <IconBase {...props}><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></IconBase>;
const CheckCircleIcon = (props: any) => <IconBase {...props}><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><path d="m9 11 3 3L22 4"/></IconBase>;
const ChevronDownIcon = (props: any) => <IconBase {...props}><polyline points="6 9 12 15 18 9"/></IconBase>;
const ChevronUpIcon = (props: any) => <IconBase {...props}><polyline points="18 15 12 9 6 15"/></IconBase>;
const ActivityIcon = (props: any) => <IconBase {...props}><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></IconBase>;
const ClockIcon = (props: any) => <IconBase {...props}><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></IconBase>;

type SentinelActionType = 'snooze' | 'mute' | 'unmute' | 'unsnooze';

const SENTINEL_YEAR_CLEAR_FIELDS = {
    'clientData.sentinelMuted': false,
    'clientData.sentinelSnoozedUntil': 0,
} as const;

/** Jahr-Docs bereinigen — sonst kann Alt-Mute beim Sync wieder in Root landen */
const clearSentinelFlagsInYearDocs = async (clientId: string) => {
    try {
        const yearsSnap = await getDocs(collection(db, 'kalkulation_mandanten', clientId, 'jahre'));
        await Promise.all(
            yearsSnap.docs.map((yearDoc) => updateDoc(yearDoc.ref, { ...SENTINEL_YEAR_CLEAR_FIELDS }))
        );
    } catch (err) {
        console.warn('Sentinel: Jahr-Docs konnten nicht bereinigt werden:', err);
    }
};

const resolveSentinelActor = (adminTeam: Array<{ name?: string; email?: string }>) => {
    const user = auth.currentUser;
    const email = (user?.email || '').trim().toLowerCase();
    const fromTeam = adminTeam.find((m) => (m.email || '').trim().toLowerCase() === email);
    const name =
        (fromTeam?.name || '').trim() ||
        (user?.displayName || '').trim() ||
        email ||
        'Unbekannt';
    return {
        email: email || 'unbekannt',
        name,
        uid: user?.uid || '',
    };
};

const formatSentinelActionAt = (raw: unknown): string => {
    const n = typeof raw === 'number' ? raw : Number(raw);
    if (!Number.isFinite(n) || n <= 0) return '—';
    try {
        return new Date(n).toLocaleString('de-DE', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    } catch {
        return '—';
    }
};

const readSentinelAudit = (client: any, safeClientData: any) => {
    const action =
        (client.sentinelLastAction as SentinelActionType | undefined) ||
        (safeClientData.sentinelLastAction as SentinelActionType | undefined) ||
        null;
    const at =
        client.sentinelLastActionAt ||
        safeClientData.sentinelLastActionAt ||
        null;
    const byName =
        client.sentinelLastActionByName ||
        safeClientData.sentinelLastActionByName ||
        '';
    const byEmail =
        client.sentinelLastActionByEmail ||
        safeClientData.sentinelLastActionByEmail ||
        '';
    return { action, at, byName: String(byName || '').trim(), byEmail: String(byEmail || '').trim() };
};

const SentinelRadar: React.FC = () => {
    const { crmClients, pendingApprovals, startImpersonation, adminTeam, fetchClients } = useAdminStore();
    const settings = useAppStore(state => state.settings);
    
    const [isOpen, setIsOpen] = useState(false);
    const [expandedClient, setExpandedClient] = useState<string | null>(null);
    const [sessionDismissed, setSessionDismissed] = useState<string[]>([]);
    const [isProcessing, setIsProcessing] = useState(false);
    const [snoozeValues, setSnoozeValues] = useState<Record<string, number>>({});
    /** Ignorierte/Pausierte nur auf Klick — nicht dauerhaft in der Hauptliste */
    const [showRestingArchive, setShowRestingArchive] = useState(false);

    const allClients = useMemo(
        () => [...(crmClients || []), ...(pendingApprovals || [])],
        [crmClients, pendingApprovals]
    );

    const radarAlerts = useMemo(() => {
        if (!settings || !settings.global) return [];
        const alerts: Array<{ client: any, issues: string[] }> = [];
        const now = new Date().getTime();

        allClients.forEach(client => {
            if (client.isArchived || client.isDeleted) return;
            
            const rawData = client.clientData && Object.keys(client.clientData).length > 0 ? client.clientData : client;
            const safeClientData = sanitizeClientData(rawData);
            
            // Mute: Root ODER clientData — beide müssen false sein (früher blieb oft nur Root false)
            const muted =
                client.sentinelMuted === true ||
                safeClientData.sentinelMuted === true;
            if (muted) return;
            if (sessionDismissed.includes(client.id)) return;

            const snoozedUntil = client.sentinelSnoozedUntil || safeClientData.sentinelSnoozedUntil;
            if (snoozedUntil && snoozedUntil > now) return;

            const clientIssues: string[] = [];
            let metrics: any = null;
            try {
                metrics = calculateAllMetrics(safeClientData, settings);
            } catch (e) {
                return;
            }

            if (!metrics) return;

            // Salon-Defizit nur bei echtem Ø-Fehlbetrag (Geld muss mitgebracht werden) —
            // nicht bei Mitarbeiter unter Ziel / 10%-Miss.
            const avgDefizit = Number(safeClientData.salonDefizitValue);
            const isDefizitaer = Number.isFinite(avgDefizit) && avgDefizit < -0.5;
            if (isDefizitaer) {
                clientIssues.push(
                    `Salon defizitär: Arbeitet nicht kostendeckend (Ø-Fehlbetrag: ${Math.abs(avgDefizit).toFixed(0)}€ netto/Monat).`
                );
            }

            if (metrics.rentabilitaetIst < 0) {
                clientIssues.push(`Rücklage Defizitär: Gesamt-Rentabilität liegt bei ${metrics.rentabilitaetIst.toFixed(1)}%. Unternehmer bringt Geld mit!`);
            }

            // 💉 2. MITARBEITER-STATUS (DIREKTER STORE-ABRUF)
            const personalData = metrics.personalData || [];
            
            personalData.forEach((ma: any) => {
                const leistung = metrics.calculatedMitarbeiterLeistung?.[ma.id];
                if (!leistung) return;

                const isHJ2 = safeClientData.isHJ2Freigegeben;
                const status = isHJ2 ? leistung.calculatedStatusHJ2 : leistung.calculatedStatusHJ1;
                const eff = (leistung.calculatedAvgEfficiency * 100).toFixed(0);

                if (status === 'schlecht') {
                    clientIssues.push(
                        `Mitarbeiter unter Ziel (${ma.name}): Ø-Leistung (${eff}%) liegt im kritischen Bereich (Status: Schlecht) — Gehaltsdeckung prüfen.`
                    );
                }
            });

            // 3. GHOSTING
            const updatedAt = client.updatedAt ? new Date(client.updatedAt).getTime() : null;
            if (updatedAt) {
                const daysSince = (now - updatedAt) / (1000 * 60 * 60 * 24);
                if (daysSince > 60) {
                    clientIssues.push(`Ghosting: Akte wurde seit ${Math.floor(daysSince)} Tagen nicht mehr bearbeitet.`);
                }
            }

            if (clientIssues.length > 0) {
                alerts.push({ client, issues: clientIssues });
            }
        });

        return alerts;
    }, [allClients, sessionDismissed, settings]);

    /** Mute / aktive Pause — inkl. wer es gesetzt hat (Nachverfolgung) */
    const restingEntries = useMemo(() => {
        const now = Date.now();
        const rows: Array<{
            client: any;
            clientName: string;
            kind: 'mute' | 'snooze';
            snoozedUntil: number | null;
            audit: ReturnType<typeof readSentinelAudit>;
        }> = [];

        allClients.forEach((client) => {
            if (client.isArchived || client.isDeleted) return;
            const rawData =
                client.clientData && Object.keys(client.clientData).length > 0
                    ? client.clientData
                    : client;
            const safeClientData = sanitizeClientData(rawData);
            const muted =
                client.sentinelMuted === true || safeClientData.sentinelMuted === true;
            const snoozedUntilRaw =
                client.sentinelSnoozedUntil || safeClientData.sentinelSnoozedUntil;
            const snoozedUntil = Number(snoozedUntilRaw) || 0;
            const snoozeActive = snoozedUntil > now;
            if (!muted && !snoozeActive) return;

            const clientName =
                `${client.vorname || ''} ${client.nachname || ''}`.trim() ||
                'Unbekannter Mandant';
            rows.push({
                client,
                clientName,
                kind: muted ? 'mute' : 'snooze',
                snoozedUntil: snoozeActive ? snoozedUntil : null,
                audit: readSentinelAudit(client, safeClientData),
            });
        });

        rows.sort((a, b) => Number(b.audit.at || 0) - Number(a.audit.at || 0));
        return rows;
    }, [allClients]);

    const buildAuditPayload = (action: SentinelActionType, extra: Record<string, unknown> = {}) => {
        const actor = resolveSentinelActor(adminTeam || []);
        const at = Date.now();
        return {
            sentinelLastAction: action,
            sentinelLastActionAt: at,
            sentinelLastActionByEmail: actor.email,
            sentinelLastActionByName: actor.name,
            sentinelLastActionByUid: actor.uid,
            'clientData.sentinelLastAction': action,
            'clientData.sentinelLastActionAt': at,
            'clientData.sentinelLastActionByEmail': actor.email,
            'clientData.sentinelLastActionByName': actor.name,
            'clientData.sentinelLastActionByUid': actor.uid,
            ...extra,
        };
    };

    const handleSnooze = async (e: React.MouseEvent, clientId: string, days: number) => {
        e.stopPropagation();
        setIsProcessing(true);
        const snoozeUntilMs = new Date().getTime() + (days * 24 * 60 * 60 * 1000);
        
        try {
            await adminUpdateMandantRoot(clientId, {
                ...buildAuditPayload('snooze', {
                    sentinelSnoozedUntil: snoozeUntilMs,
                    'clientData.sentinelSnoozedUntil': snoozeUntilMs,
                    sentinelLastActionDays: days,
                    'clientData.sentinelLastActionDays': days,
                }),
            });
            setSessionDismissed(prev => [...prev, clientId]);
            if (expandedClient === clientId) setExpandedClient(null);
            await fetchClients();
        } catch (error) {
            console.error("Fehler beim Snoozen:", error);
        } finally {
            setIsProcessing(false);
        }
    };

    const handleMuteForever = async (e: React.MouseEvent, clientId: string, clientName: string) => {
        e.stopPropagation();
        if (!window.confirm(`Radar für "${clientName}" dauerhaft stummschalten? Du erhältst keine Warnungen mehr.`)) return;
        
        setIsProcessing(true);
        try {
            await adminUpdateMandantRoot(clientId, {
                ...buildAuditPayload('mute', {
                    sentinelMuted: true,
                    'clientData.sentinelMuted': true,
                }),
            });
            setSessionDismissed(prev => [...prev, clientId]);
            if (expandedClient === clientId) setExpandedClient(null);
            await fetchClients();
        } catch (error) {
            console.error("Fehler beim Muten:", error);
        } finally {
            setIsProcessing(false);
        }
    };

    const handleReactivate = async (
        e: React.MouseEvent,
        clientId: string,
        clientName: string,
        kind: 'mute' | 'snooze'
    ) => {
        e.stopPropagation();
        const label = kind === 'mute' ? 'dauerhafte Stummschaltung' : 'Pause';
        if (!window.confirm(`Radar für "${clientName}" wieder aktivieren (${label} aufheben)?`)) return;

        setIsProcessing(true);
        try {
            await adminUpdateMandantRoot(clientId, {
                ...buildAuditPayload(kind === 'mute' ? 'unmute' : 'unsnooze', {
                    sentinelMuted: false,
                    'clientData.sentinelMuted': false,
                    sentinelSnoozedUntil: 0,
                    'clientData.sentinelSnoozedUntil': 0,
                }),
            });
            await clearSentinelFlagsInYearDocs(clientId);
            setSessionDismissed((prev) => prev.filter((id) => id !== clientId));
            await fetchClients();
        } catch (error) {
            console.error('Fehler beim Reaktivieren:', error);
            alert('Reaktivieren fehlgeschlagen. Bitte erneut versuchen.');
        } finally {
            setIsProcessing(false);
        }
    };

    const handleOpenAkte = (e: React.MouseEvent, clientId: string, clientName: string) => {
        e.stopPropagation();
        startImpersonation(clientId, clientName);
        setShowRestingArchive(false);
        setIsOpen(false);
    };

    return (
        <>
            <button 
                onClick={() => setIsOpen(true)}
                className={`w-full flex items-center justify-between px-4 py-3 rounded-lg border transition-all shadow-lg group ${radarAlerts.length > 0 ? 'bg-red-50 border-red-500 text-red-800 hover:bg-red-100 dark:bg-red-900/10 dark:border-red-500/50 dark:hover:bg-red-900/20 dark:text-red-400' : 'bg-pe-card border-pe-border-strong text-pe-text hover:bg-pe-hover'}`}
            >
                <div className="flex items-center gap-3">
                    <ActivityIcon size={18} className={radarAlerts.length > 0 ? "text-red-600 dark:text-red-500 animate-pulse" : "text-pe-muted"} />
                    <span className={`text-sm font-bold ${radarAlerts.length > 0 ? 'text-red-800 dark:text-red-400' : 'text-pe-text'}`}>Sentinel Radar</span>
                </div>
                {radarAlerts.length > 0 && (
                    <span className="bg-red-500 text-white text-[10px] font-black px-2 py-0.5 rounded shadow-[0_0_10px_rgba(239,68,68,0.5)]">
                        {radarAlerts.length}
                    </span>
                )}
            </button>

            {/* Portal auf document.body: Die Sidebar ist position:sticky und bildet
                einen eigenen Stacking-Context — ohne Portal landet das Modal trotz
                z-Index HINTER dem Hauptinhalt (nur der dunkle Backdrop war sichtbar) */}
            {isOpen && createPortal(
                <div className="fixed inset-0 z-[100000] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in text-left">
                    <div className="bg-[#0f1015] border border-slate-800 rounded-2xl w-full max-w-2xl shadow-[0_0_50px_rgba(0,0,0,0.8)] flex flex-col max-h-[85vh]">
                        
                        <div className="p-6 border-b border-slate-800 flex justify-between items-center shrink-0 bg-[#15171e] rounded-t-2xl">
                            <div>
                                <div className="text-[10px] text-red-500 font-black tracking-[0.3em] uppercase mb-1 flex items-center gap-2">
                                    <AlertTriangleIcon size={12} /> Frühwarnsystem Aktiv
                                </div>
                                <h2 className="text-xl font-bold text-white tracking-widest uppercase">Radar Warnungen</h2>
                            </div>
                            <button
                                onClick={() => {
                                    setShowRestingArchive(false);
                                    setIsOpen(false);
                                }}
                                className="p-2 bg-slate-800 hover:bg-slate-700 rounded-full text-slate-400 transition-colors"
                            >
                                <XIcon size={20} />
                            </button>
                        </div>

                        <div className="flex-1 overflow-y-auto p-6 space-y-4">
                            {radarAlerts.length === 0 ? (
                                <div className="text-center py-12">
                                    <CheckCircleIcon size={48} className="mx-auto text-emerald-600/50 mb-4" />
                                    <h3 className="text-slate-300 font-bold uppercase tracking-widest">Alles im grünen Bereich</h3>
                                    <p className="text-xs text-slate-500 mt-2">Das Sentinel-Radar überwacht das System. Aktuell keine kritischen Totpunkte unterschritten.</p>
                                </div>
                            ) : (
                                radarAlerts.map(({ client, issues }) => {
                                    const isExpanded = expandedClient === client.id;
                                    const clientName = `${client.vorname || ''} ${client.nachname || ''}`.trim() || 'Unbekannter Mandant';
                                    
                                    return (
                                        <div key={client.id} className="bg-[#15171e] border border-slate-800 rounded-xl overflow-hidden shadow-lg transition-all">
                                            <div 
                                                className="p-4 flex justify-between items-center cursor-pointer hover:bg-slate-800/50 transition-colors"
                                                onClick={() => setExpandedClient(isExpanded ? null : client.id)}
                                            >
                                                <div className="flex items-center gap-4">
                                                    <div className="w-2 h-2 rounded-full bg-red-500 shadow-[0_0_10px_rgba(239,68,68,0.8)] animate-pulse"></div>
                                                    <div>
                                                        <div className="font-bold text-white text-sm">{clientName}</div>
                                                        <div className="text-[10px] text-slate-500 font-mono mt-1">ID: {client.mandantNr || client.id.substring(0,8)}</div>
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-4">
                                                    <span className="text-[10px] font-black bg-red-900/30 text-red-400 border border-red-800/50 px-2 py-1 rounded uppercase tracking-widest">
                                                        {issues.length} Probleme
                                                    </span>
                                                    <span className="text-slate-500">
                                                        {isExpanded ? <ChevronUpIcon size={18} /> : <ChevronDownIcon size={18} />}
                                                    </span>
                                                </div>
                                            </div>

                                            {isExpanded && (
                                                <div className="p-4 border-t border-slate-800 bg-[#0c0d11]">
                                                    <ul className="space-y-2 mb-6">
                                                        {issues.map((issue, idx) => (
                                                            <li key={idx} className="text-sm text-slate-300 flex items-start gap-2">
                                                                <span className="text-red-500 mt-1">•</span> {issue}
                                                            </li>
                                                        ))}
                                                    </ul>
                                                    
                                                    <div className="flex flex-wrap items-center gap-3">
                                                        <button 
                                                            onClick={(e) => handleOpenAkte(e, client.id, clientName)}
                                                            className="flex-1 sm:flex-none flex items-center justify-center gap-2 bg-[#E6C299] hover:bg-white text-black px-4 py-2 rounded text-[10px] font-black uppercase tracking-widest transition-colors shadow-lg"
                                                        >
                                                            <ExternalLinkIcon size={14} /> Akte öffnen
                                                        </button>
                                                        
                                                        <div className="flex-1 sm:flex-none flex items-center bg-slate-800 rounded border border-slate-700 overflow-hidden">
                                                            <div className="px-3 flex items-center text-slate-400">
                                                                <ClockIcon size={14} />
                                                            </div>
                                                            <select
                                                                value={snoozeValues[client.id] || 60}
                                                                onChange={(e) => setSnoozeValues(prev => ({...prev, [client.id]: parseInt(e.target.value)}))}
                                                                className="bg-transparent text-slate-300 text-[10px] font-bold uppercase tracking-widest outline-none cursor-pointer py-2 pr-2 appearance-none"
                                                                onClick={(e) => e.stopPropagation()}
                                                            >
                                                                <option value={30}>1 Monat</option>
                                                                <option value={60}>2 Monate</option>
                                                                <option value={90}>3 Monate</option>
                                                            </select>
                                                            <button 
                                                                onClick={(e) => handleSnooze(e, client.id, snoozeValues[client.id] || 60)}
                                                                disabled={isProcessing}
                                                                className="bg-slate-700 hover:bg-slate-600 text-white px-4 py-2 text-[10px] font-black uppercase tracking-widest transition-colors border-l border-slate-600"
                                                            >
                                                                Erledigt / Pausieren
                                                            </button>
                                                        </div>
                                                        
                                                        <button 
                                                            onClick={(e) => handleMuteForever(e, client.id, clientName)}
                                                            disabled={isProcessing}
                                                            className="w-full sm:w-auto flex items-center justify-center gap-2 bg-red-900/10 hover:bg-red-900/30 text-red-500 border border-red-900/50 hover:border-red-500 px-4 py-2 rounded text-[10px] font-black uppercase tracking-widest transition-colors sm:ml-auto"
                                                            title="Radar für diesen Mandanten dauerhaft abschalten"
                                                        >
                                                            <BellOffIcon size={14} /> Ignorieren
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })
                            )}

                            {restingEntries.length > 0 && (
                                <div className="pt-4 mt-2 border-t border-slate-800">
                                    <button
                                        type="button"
                                        onClick={() => setShowRestingArchive((v) => !v)}
                                        className="w-full flex items-center justify-between gap-3 rounded-lg border border-slate-800 bg-[#12141a] px-3 py-2.5 text-left hover:bg-slate-800/40 transition-colors"
                                    >
                                        <span className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500">
                                            Archiv · Ruhend / Ignoriert ({restingEntries.length})
                                        </span>
                                        <span className="text-slate-500 shrink-0">
                                            {showRestingArchive ? (
                                                <ChevronUpIcon size={16} />
                                            ) : (
                                                <ChevronDownIcon size={16} />
                                            )}
                                        </span>
                                    </button>
                                    {showRestingArchive && (
                                        <div className="space-y-2 mt-3">
                                            {restingEntries.map(({ client, clientName, kind, snoozedUntil, audit }) => {
                                                const who =
                                                    [audit.byName, audit.byEmail].filter(Boolean).join(' · ') ||
                                                    'Unbekannt (Sync-Altflag / vor Audit-Tracking)';
                                                const when = formatSentinelActionAt(audit.at);
                                                const kindLabel =
                                                    kind === 'mute'
                                                        ? 'Ignoriert (dauerhaft)'
                                                        : `Pausiert bis ${formatSentinelActionAt(snoozedUntil)}`;
                                                return (
                                                    <div
                                                        key={`rest-${client.id}`}
                                                        className="rounded-lg border border-slate-800 bg-[#12141a] px-3 py-2.5"
                                                    >
                                                        <div className="flex flex-wrap items-center justify-between gap-2">
                                                            <p className="text-sm font-bold text-slate-200">{clientName}</p>
                                                            <span className="text-[10px] font-black uppercase tracking-wider text-amber-500/90">
                                                                {kindLabel}
                                                            </span>
                                                        </div>
                                                        <p className="mt-1 text-xs text-slate-500">
                                                            Von: <span className="text-slate-300">{who}</span>
                                                            {' · '}
                                                            Am: <span className="text-slate-300">{when}</span>
                                                        </p>
                                                        <button
                                                            type="button"
                                                            disabled={isProcessing}
                                                            onClick={(e) => handleReactivate(e, client.id, clientName, kind)}
                                                            className="mt-2 text-[10px] font-black uppercase tracking-wider text-emerald-400/90 hover:text-emerald-300 border border-emerald-500/30 hover:border-emerald-400/50 rounded-md px-2.5 py-1 transition-colors disabled:opacity-40"
                                                        >
                                                            Wieder aktivieren
                                                        </button>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </>
    );
};

export default SentinelRadar;